/**
 * KioskExitDialog — auth challenge to exit kiosk mode (ADR-073 Decision 5).
 *
 * Staff must enter valid credentials to leave kiosk mode. Only users with
 * kiosk-activation permission can exit. Failed attempts are logged.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Field,
  Heading,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useKiosk } from '../../contexts/KioskContext';
import { deviceIdFetch } from '../../lib/deviceIdHeader';
import { toaster } from '../ui/toaster';
import { useNavigate } from 'react-router-dom';

interface KioskExitDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KioskExitDialog({ isOpen, onClose }: KioskExitDialogProps) {
  const { t } = useTranslation();
  const { exitKioskMode } = useKiosk();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // `deviceIdFetch` stamps `x-device-id` so the credential
      // check's server-side session mint (and its login-activity row) is
      // bound to this kiosk browser's device identity.
      const response = await deviceIdFetch('/rest/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        setError(t('kiosk.invalidCredentials', 'Invalid credentials. Please try again.'));
        setIsLoading(false);
        return;
      }

      // Auth succeeded — exit kiosk mode
      exitKioskMode();
      toaster.create({
        title: t('kiosk.modeDeactivatedTitle', 'Kiosk mode deactivated'),
        type: 'success',
      });
      onClose();
      navigate('/', { replace: true });
      setEmail('');
      setPassword('');
    } catch {
      setError(t('kiosk.authFailed', 'Authentication failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setError('');
    onClose();
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title={t('kiosk.exitKioskMode', 'Exit Kiosk Mode')}
      size="sm"
      aria-label={t('kiosk.exitKioskDialogAria', 'Exit kiosk mode dialog')}
    >
      <VStack gap={6} align="stretch">
        <Box>
          <Heading as="h3" size="sm" mb={2}>
            {t('kiosk.staffAuthRequired', 'Staff Authentication Required')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {t('kiosk.exitAuthInstructions', 'Enter your staff credentials to exit kiosk mode and return to the SMS admin interface.')}
          </Text>
        </Box>

        <form onSubmit={handleSubmit}>
          <VStack gap={4} align="stretch">
            <Field.Root invalid={!!error}>
              <Field.Label>{t('auth.email', 'Email')}</Field.Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder', 'staff@school.edu')}
                autoComplete="username"
                required
              />
            </Field.Root>

            <Field.Root invalid={!!error}>
              <Field.Label>{t('auth.password', 'Password')}</Field.Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder', 'Password')}
                autoComplete="current-password"
                required
              />
              {error && <Field.ErrorText data-e2e="kiosk-exit-error">{error}</Field.ErrorText>}
            </Field.Root>

            <ButtonComponent
              visual="primary"
              type="submit"
              disabled={isLoading || !email || !password}
              minH="44px"
              minW="44px"
              data-e2e="exit-kiosk-button">
              {isLoading ? t('common.authenticating', 'Authenticating...') : t('kiosk.exitKioskMode', 'Exit Kiosk Mode')}
            </ButtonComponent>
          </VStack>
        </form>
      </VStack>
    </DrawerComponent>
  );
}
